import { Stack } from "@mui/material";
import { register } from "./registration";
import { useTranslation } from "react-i18next";
import { FormProvider, useController, useForm } from "react-hook-form";
import { mf } from "../../constants";
import { sendErrorToBackend } from "@newsenec/logging";
import { RegistrationStep } from "./RegistrationScreen";
import { SenecButton, SenecTextField } from "@newsenec/ui-components";
import { Dispatch, SetStateAction } from "react";

interface FormData {
    devNumber: string;
    invitationCode: string;
}

export const RegistrationForm = ({
    setRegistrationStep,
    setError,
    initialValues,
}: {
    setRegistrationStep: Dispatch<SetStateAction<RegistrationStep>>;
    setError: Dispatch<SetStateAction<string | undefined>>;
    initialValues?: FormData;
}) => {
    const { t } = useTranslation();

    const onSubmit = (values: FormData) => {
        setRegistrationStep("registering");

        register(Number(values.devNumber), values.invitationCode)
            .then(() => {
                setRegistrationStep("success");
            })
            .catch((error) => {
                sendErrorToBackend(
                    mf,
                    `Could not register user with devNumber "${values.devNumber}": ${error?.message || "Unknown error."}`,
                    error?.stack,
                );

                // reset old error message
                setError(undefined);
                if (error.response?.status === 403) {
                    setError(t("retryScreenMessage403"));
                }
                setRegistrationStep("error");
            });
    };

    const formProvider = useForm<FormData>({
        defaultValues: initialValues ?? {
            devNumber: "",
            invitationCode: "",
        },
    });

    return (
        <FormProvider {...formProvider}>
            <form onSubmit={formProvider.handleSubmit((values) => onSubmit(values))}>
                <Stack spacing={2}>
                    <DevNumberField />
                    <InvitationCodeField />
                    <RegisterButton />
                </Stack>
            </form>
        </FormProvider>
    );
};

const DevNumberField = () => {
    const { t } = useTranslation();
    const { field } = useController<FormData>({ name: "devNumber" });

    return (
        <SenecTextField
            variant="outlined"
            {...field}
            required
            type="number"
            label={t("devNumberLabel")}
            placeholder={t("devNumberFieldPlaceholder")}
            sx={{ minWidth: "250px" }}
        />
    );
};

const InvitationCodeField = () => {
    const { t } = useTranslation();
    const { field } = useController<FormData>({ name: "invitationCode" });

    return (
        <SenecTextField
            variant="outlined"
            {...field}
            required
            type="text"
            label={t("invitationCodeLabel")}
            placeholder={t("invitationCodeFieldPlaceholder")}
            sx={{ minWidth: "250px" }}
        />
    );
};

const RegisterButton = () => {
    const { t } = useTranslation();
    return (
        <SenecButton type="submit" level="primary">
            {t("registerLabel")}
        </SenecButton>
    );
};
